/**
 * API Client Configuration
 * Centralized API client setup for making HTTP requests
 */

import { cognitoAuthService } from '../services/cognitoAuth.service';
import { isCognitoConfigured } from '../config/cognito';
import { isTokenExpiringSoon } from '../utils/jwtExpiry';

// API base URL. For dev/testing the fallback is used. When deploying to production, set REACT_APP_API_URL at build time.
const DEV_API_URL_FALLBACK = 'https://62n4fo9ls4.execute-api.us-east-1.amazonaws.com/dev';
const PROD_API_URL_FALLBACK = 'https://uu49nfgwb3.execute-api.us-east-1.amazonaws.com/prod';

const PROD_HOSTS = ['spinoraofficial.com', 'www.spinoraofficial.com', 'app.spinoraofficial.com', 'dksd42slr2nb5.cloudfront.net'];
const DEV_API_HINTS = ['62n4fo9ls4', '/dev'];

function getApiBaseUrl(): string {
  const envUrl = process.env.REACT_APP_API_URL;
  const envValid = envUrl && String(envUrl).startsWith('http');
  const useProdOnLocalhost = /^(1|true|yes)$/i.test(String(process.env.REACT_APP_USE_PROD_ON_LOCALHOST || ''));
  if (typeof window !== 'undefined') {
    const host = window.location.hostname || '';
    if (host === 'localhost' || host === '127.0.0.1') {
      if (useProdOnLocalhost) return envValid && !DEV_API_HINTS.some((h) => String(envUrl).includes(h))
        ? String(envUrl).trim().replace(/\/$/, '') : PROD_API_URL_FALLBACK;
      return DEV_API_URL_FALLBACK;
    }
    if (PROD_HOSTS.includes(host)) {
      // Safeguard: on prod domains, never use dev API even if REACT_APP_API_URL is misconfigured
      if (envValid && DEV_API_HINTS.some((h) => String(envUrl).includes(h))) return PROD_API_URL_FALLBACK;
      return envValid ? String(envUrl).trim().replace(/\/$/, '') : PROD_API_URL_FALLBACK;
    }
    return envValid ? String(envUrl).trim().replace(/\/$/, '') : DEV_API_URL_FALLBACK;
  }
  return envValid ? String(envUrl).trim().replace(/\/$/, '') : DEV_API_URL_FALLBACK;
}
const API_BASE_URL = getApiBaseUrl();

export interface ApiError {
  message: string;
  status?: number;
  code?: string;
  /** Optional technical detail from server (e.g. QuickBooks API error snippet). */
  detail?: string;
  /** AI Pro: when usage limit reached (429), date when usage resets (YYYY-MM-DD). */
  resetDate?: string;
}

/** True if the stored auth token looks like a JWT (do not overwrite with placeholders). */
export function isStoredAuthTokenJwt(): boolean {
  const raw = typeof localStorage !== 'undefined' ? localStorage.getItem('authToken') : null;
  return looksLikeJwt(raw);
}

/** True if a string looks like a JWT (3 base64url parts). Used to avoid showing "session ended" for dev-mode/placeholders. */
export function looksLikeJwt(raw: string | null): boolean {
  if (!raw || typeof raw !== 'string') return false;
  const parts = raw.trim().split('.');
  return parts.length === 3 && parts.every((p) => /^[A-Za-z0-9_-]+$/.test(p));
}

class ApiClient {
  private baseURL: string;

  constructor(baseURL: string) {
    this.baseURL = baseURL;
  }

  /** Refresh token if it expires within 20 min — proactive refresh avoids mid-action 401 (industry: refresh well before expiry). */
  private async ensureTokenFresh(): Promise<void> {
    if (!isCognitoConfigured() || !isStoredAuthTokenJwt()) return;
    const token = typeof localStorage !== 'undefined' ? localStorage.getItem('authToken') : null;
    if (!token || !isTokenExpiringSoon(token, 20 * 60 * 1000)) return;
    try {
      const session = await cognitoAuthService.getCurrentSession();
      const newToken = session.getIdToken().getJwtToken();
      if (newToken && typeof localStorage !== 'undefined') localStorage.setItem('authToken', newToken);
    } catch {
      /* ignore — will retry on 401 if needed */
    }
  }

  private async request<T>(
    endpoint: string,
    options: (RequestInit & { silent401?: boolean }) = {},
    is401Retry = false
  ): Promise<T> {
    await this.ensureTokenFresh();
    const { silent401, ...fetchOptions } = options;
    const token = this.getToken();

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(fetchOptions.headers as Record<string, string>),
    };

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const isGet = fetchOptions.method == null || fetchOptions.method === 'GET';
    const requestInit: RequestInit = {
      ...fetchOptions,
      headers,
      ...(token && isGet ? { cache: 'no-store' as RequestCache } : {}),
    };
    let response = await fetch(`${this.baseURL}${endpoint}`, requestInit);

    // Retry transient 5xx up to 2 times with exponential backoff (1s, 2s)
    if (response.status >= 500 && response.status < 600 && !is401Retry) {
      for (const delayMs of [1000, 2000]) {
        await new Promise((r) => setTimeout(r, delayMs));
        response = await fetch(`${this.baseURL}${endpoint}`, requestInit);
        if (response.status < 500 || response.status >= 600) break;
      }
    }

    if (!response.ok) {
      const error: ApiError = {
        message: `API Error: ${response.statusText}`,
        status: response.status,
      };

      try {
        const text = await response.text();
        try {
          const errorData = JSON.parse(text) as { message?: string; code?: string; detail?: string; resetDate?: string };
          error.message = errorData.message || error.message;
          error.code = errorData.code;
          error.detail = errorData.detail;
          error.resetDate = errorData.resetDate;
        } catch {
          if (text && text.length < 300) error.message = text;
        }
        if (response.status >= 500 && /internal server error|api error/i.test(error.message) && !error.detail) {
          error.message = 'Something went wrong. Please try again.';
        }
      } catch {
        // ignore
      }

      // 401: try token refresh before signing out (avoids mid-invoice session end from transient failures)
      // silent401: background session check — don't clear token or show "session ended" for generic 401s (avoids false positives).
      // SESSION_INVALIDATED (signed in on another device): always clear and show modal, even when silent401 — that's the poll's purpose.
      if (response.status === 401 && typeof window !== 'undefined') {
        const hadRealSession = isStoredAuthTokenJwt();
        if (silent401 && error.code !== 'SESSION_INVALIDATED') {
          // Background poll: generic 401 — don't invalidate; user-initiated calls will still trigger session-ended
          throw error;
        }
        // SESSION_INVALIDATED: refresh won't help (auth_time is baked in); skip retries and clear immediately
        if (error.code === 'SESSION_INVALIDATED') {
          if (hadRealSession) {
            localStorage.setItem('spinora-session-invalidated-reason', 'SESSION_INVALIDATED');
            localStorage.removeItem('authToken');
          }
          window.dispatchEvent(new CustomEvent('spinora-session-invalidated', { detail: { message: error.message, code: error.code } }));
          throw error;
        }
        if (!is401Retry && isCognitoConfigured() && hadRealSession) {
          const tryRefresh = async (): Promise<boolean> => {
            try {
              const session = await cognitoAuthService.getCurrentSession();
              const newToken = session.getIdToken().getJwtToken();
              if (newToken) {
                localStorage.setItem('authToken', newToken);
                return true;
              }
            } catch {
              /* refresh failed */
            }
            return false;
          };
          if (await tryRefresh()) return this.request<T>(endpoint, options, true);
          await new Promise((r) => setTimeout(r, 500));
          if (await tryRefresh()) return this.request<T>(endpoint, options, true);
          await new Promise((r) => setTimeout(r, 1000));
          if (await tryRefresh()) return this.request<T>(endpoint, options, true);
          await new Promise((r) => setTimeout(r, 2000));
          if (await tryRefresh()) return this.request<T>(endpoint, options, true);
        }
        if (hadRealSession) {
          localStorage.removeItem('spinora-session-invalidated-reason'); // clear so other tabs don't inherit wrong reason
          localStorage.removeItem('authToken');
        }
        // Only show session-ended modal for SESSION_INVALIDATED (signed in on another device). Other 401s (expired, invalid) — throw and let caller handle; avoid false "session ended" on rapid actions.
        if (error.code === 'SESSION_INVALIDATED') {
          window.dispatchEvent(new CustomEvent('spinora-session-invalidated', { detail: { message: error.message, code: error.code } }));
        }
      }

      // 402: subscription inactive (expired, canceled, or past_due past grace) — redirect to billing resolution
      if (response.status === 402 && typeof window !== 'undefined' && error.code === 'SUBSCRIPTION_INACTIVE') {
        window.dispatchEvent(new CustomEvent('spinora-subscription-inactive', { detail: { message: error.message, code: error.code } }));
      }

      throw error;
    }

    const contentType = response.headers.get('content-type');
    const text = await response.text();
    if (contentType && contentType.includes('application/json')) {
      try {
        return (text ? JSON.parse(text) : {}) as T;
      } catch {
        const err = new Error('Invalid response from server.') as Error & { status?: number };
        err.status = response.status;
        throw err;
      }
    }
    return text as unknown as T;
  }

  private getToken(): string | null {
    const raw = localStorage.getItem('authToken');
    if (!raw || typeof raw !== 'string') return null;
    // Only send if it looks like a JWT (3 parts). Rejects placeholders like "dev-mode" or "free-trial-token"
    const parts = raw.trim().split('.');
    if (parts.length !== 3 || !parts.every((p) => /^[A-Za-z0-9_-]+$/.test(p))) return null;
    return raw;
  }

  async get<T>(endpoint: string, options?: RequestInit & { silent401?: boolean }): Promise<T> {
    return this.request<T>(endpoint, { ...options, method: 'GET' });
  }

  async post<T>(endpoint: string, data?: unknown, options?: RequestInit): Promise<T> {
    return this.request<T>(endpoint, {
      ...options,
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async put<T>(endpoint: string, data?: unknown, options?: RequestInit): Promise<T> {
    return this.request<T>(endpoint, {
      ...options,
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async patch<T>(endpoint: string, data?: unknown, options?: RequestInit): Promise<T> {
    return this.request<T>(endpoint, {
      ...options,
      method: 'PATCH',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async delete<T>(endpoint: string, options?: RequestInit): Promise<T> {
    return this.request<T>(endpoint, { ...options, method: 'DELETE' });
  }
}

export const apiClient = new ApiClient(API_BASE_URL);

/**
 * Call before long operations (e.g. voice processing) to refresh token if expiring soon.
 * If session is invalid, dispatches session-invalidated and returns false.
 * Returns true if token is fresh or refresh succeeded.
 */
export async function ensureFreshTokenForLongOperation(): Promise<boolean> {
  if (typeof window === 'undefined') return true;
  if (!isCognitoConfigured() || !isStoredAuthTokenJwt()) return true;
  const token = localStorage.getItem('authToken');
  if (!token || !looksLikeJwt(token)) return true;
  if (!isTokenExpiringSoon(token, 2 * 60 * 1000)) return true; // 2 min buffer — refresh if expiring soon
  try {
    const session = await cognitoAuthService.getCurrentSession();
    const newToken = session.getIdToken().getJwtToken();
    if (newToken) {
      localStorage.setItem('authToken', newToken);
      return true;
    }
  } catch {
    /* session invalid */
  }
  localStorage.removeItem('authToken');
  window.dispatchEvent(new CustomEvent('spinora-session-invalidated', { detail: { message: 'Your session expired. Please sign in again.' } }));
  return false;
}

/** Call in catch blocks: only dispatch session-invalidated for SESSION_INVALIDATED (signed in on another device). Other 401s — do not show modal; caller can show "try again" etc. */
export function dispatchSessionInvalidatedIf401(err: unknown): void {
  const status = (err as { status?: number })?.status;
  const code = (err as { code?: string })?.code;
  if (status === 401 && code === 'SESSION_INVALIDATED' && typeof window !== 'undefined') {
    const msg = (err as { message?: string })?.message;
    window.dispatchEvent(new CustomEvent('spinora-session-invalidated', { detail: { message: msg, code } }));
  }
}

